import { useParams, Link, useNavigate } from "react-router-dom";
import { RequestStatusBadge } from "@/components/status-badges";
import { useState, useRef } from "react";
import {
  useMomentRequest,
  useUpdateMomentRequest,
  useDeleteMomentRequest,
  useAddMomentVideo,
  useDeleteMomentVideo,
  useClearVideos,
} from "@/pages/moment-request/hooks/useMomentRequests";
import { useUploadFile } from "@groot/shell/pages/storage/hooks/useStorage";
import { Button } from "@groot/ui/button";
import { toast } from "sonner";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@groot/ui/dialog";
import { Label } from "@groot/ui/label";
import { Textarea } from "@groot/ui/textarea";
import {
  ArrowLeft,
  Upload,
  Trash2,
  CheckCircle,
  Copy,
  ShieldAlert,
  Download,
  QrCode,
  Loader2,
  SearchX,
} from "lucide-react";
import { Card } from "@groot/ui/card";
import { EmptyState } from "@groot/ui/empty-state";
import { SkeletonCard } from "@groot/ui/loading-skeleton";
import { titleClass } from "@groot/ui/typography";
import { cn } from "@groot/ui/lib/utils";

export function MomentRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const requestId = Number(id);
  const { data: request, isLoading } = useMomentRequest(requestId);
  const updateMomentRequest = useUpdateMomentRequest();
  const deleteMomentRequest = useDeleteMomentRequest();
  const addVideo = useAddMomentVideo();
  const deleteVideo = useDeleteMomentVideo();
  const clearVideos = useClearVideos();
  const uploadFile = useUploadFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [clearNote, setClearNote] = useState("");
  const [showClearDialog, setShowClearDialog] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !request) return;

    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const filePath = `moment-requests/${request.id}/${Date.now()}-${sanitized}`;

        await uploadFile.mutateAsync({ file, filePath });

        await addVideo.mutateAsync({
          momentRequestId: request.id,
          fileName: file.name,
          videoUrl: filePath,
        });
      }
      toast.success("Uploaded", { description: "Videos uploaded successfully" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Failed to upload videos" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const completeRequest = async () => {
    try {
      await updateMomentRequest.mutateAsync({ id: requestId, data: { status: "completed" } });
      toast.success("Completed", { description: "Moment request marked as completed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to complete" });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMomentRequest.mutateAsync(requestId);
      toast.success("Deleted", { description: "Moment request deleted" });
      navigate("/moment-requests");
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to delete" });
    }
  };

  const handleClearVideos = async () => {
    try {
      await clearVideos.mutateAsync({ momentRequestId: requestId, note: clearNote });
      toast.success("Cleared", { description: "All videos have been permanently deleted" });
      setShowClearDialog(false);
      setClearNote("");
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to clear videos" });
    }
  };

  const removeVideo = async (videoId: number) => {
    try {
      await deleteVideo.mutateAsync({ momentRequestId: requestId, videoId });
      toast.success("Removed", { description: "Video removed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Failed to remove video" });
    }
  };

  const copyPublicLink = () => {
    if (!request) return;
    navigator.clipboard.writeText(
      `${window.location.origin}/public/moment-requests/${request.publicLink}`,
    );
    toast.success("Copied", { description: "Upload link copied to clipboard" });
  };

  const copyViewLink = () => {
    if (!request) return;
    navigator.clipboard.writeText(
      `${window.location.origin}/public/moment-requests/view/${request.viewLink}`,
    );
    toast.success("Copied", { description: "View link copied to clipboard" });
  };

  const downloadQrCode = () => {
    if (!request) return;
    const viewUrl = `${window.location.origin}/public/moment-requests/view/${request.viewLink}`;
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&color=000000&bgcolor=ffffff&data=${encodeURIComponent(viewUrl)}`;

    fetch(qrSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `moment-request-${request.id}-qr.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => {
        toast.error("Error", { description: "Failed to download QR code" });
      });
  };

  if (isLoading) {
    return (
      <PageLayout title="Moment Request" description="Loading...">
        <SkeletonCard lines={4} />
      </PageLayout>
    );
  }

  if (!request) {
    return (
      <PageLayout title="Moment Request" description="Not found">
        <EmptyState
          icon={SearchX}
          title="Moment request not found"
          description="This request may have been deleted or the link is invalid."
          action={
            <Button variant="outline" asChild>
              <Link to="/moment-requests">
                <ArrowLeft className="size-4" />
                Back to Moments
              </Link>
            </Button>
          }
        />
      </PageLayout>
    );
  }

  const isCompleted = request.status === "completed";
  const hasVideos = request.videos && request.videos.length > 0;
  const viewUrl = `${window.location.origin}/public/moment-requests/view/${request.viewLink}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&color=000000&bgcolor=ffffff&data=${encodeURIComponent(viewUrl)}`;
  const videosCleared = request.videosClearedAt != null;

  return (
    <PageLayout
      title={`Moment Request #${request.id}`}
      description={request.customer?.name ? `for ${request.customer.name}` : undefined}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/moment-requests">
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
        </div>

        {/* Metadata & QR */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="gap-0 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className={titleClass}>Details</h2>
              <RequestStatusBadge status={request.status} />
            </div>
            <div className="text-sm text-muted-foreground space-y-1.5">
              <p>
                <span className="font-medium text-foreground">Customer: </span>
                {request.customer?.name}
                {request.customer?.mobileNumber && ` — ${request.customer.mobileNumber}`}
              </p>
              <p>
                <span className="font-medium text-foreground">Created: </span>
                {new Date(request.createdAt).toLocaleDateString()}
              </p>
              {request.completedAt && (
                <p>
                  <span className="font-medium text-foreground">Completed: </span>
                  {new Date(request.completedAt).toLocaleDateString()}
                </p>
              )}
              {videosCleared && (
                <p className="text-warning">
                  Videos cleared — {request.videosClearedCount} deleted
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={copyPublicLink}>
                <Copy className="size-3.5" />
                Copy Upload Link
              </Button>
              <Button variant="outline" size="sm" onClick={copyViewLink}>
                <Copy className="size-3.5" />
                Copy View Link
              </Button>
              <Button variant="outline" size="sm" onClick={downloadQrCode}>
                <Download className="size-3.5" />
                Download QR
              </Button>
            </div>
          </Card>

          <Card className="gap-0 p-5 flex flex-col items-center">
            <div className="flex items-center gap-2 mb-3">
              <QrCode className="size-4 text-muted-foreground" />
              <h2 className={titleClass}>QR Code</h2>
            </div>
            <div className="border border-border rounded-xl p-3 bg-white">
              <img src={qrSrc} alt="Moment Request QR Code" className="block w-[200px] h-[200px]" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Scan to view the video</p>
          </Card>
        </div>

        {/* Upload zone */}
        {!isCompleted && !videosCleared && (
          <Card className="gap-0 p-5">
            <h2 className={cn(titleClass, "mb-3")}>Upload Videos</h2>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => !isUploading && fileInputRef.current?.click()}
              disabled={isUploading}
              className="w-full border-2 border-dashed border-border rounded-xl py-8 flex flex-col items-center gap-2 hover:border-foreground/30 hover:bg-muted/50 disabled:pointer-events-none"
            >
              {isUploading ? (
                <>
                  <Loader2 className="size-6 text-muted-foreground animate-spin" />
                  <span className="text-sm font-medium text-foreground">Uploading…</span>
                </>
              ) : (
                <>
                  <Upload className="size-6 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">
                    Click to upload videos
                  </span>
                  <span className="text-xs text-muted-foreground">
                    MP4, MOV, WEBM · up to 200MB
                  </span>
                </>
              )}
            </button>
          </Card>
        )}

        {/* Videos */}
        {hasVideos && (
          <Card className="gap-0 p-5">
            <h2 className={cn(titleClass, "mb-3")}>Videos ({request.videos!.length})</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {request.videos!.map((video) => (
                <div key={video.id} className="relative rounded-xl overflow-hidden bg-black">
                  <video
                    src={`/api/v1/moment-requests/${requestId}/files/${video.id}`}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full aspect-video"
                  />
                  <div className="px-3 py-2 bg-background/90 backdrop-blur-sm">
                    <p className="text-xs text-muted-foreground truncate">{video.fileName}</p>
                  </div>
                  {!isCompleted && !videosCleared && (
                    <button
                      type="button"
                      onClick={() => removeVideo(video.id)}
                      className="absolute top-2 right-2 size-7 rounded-full bg-white/90 shadow flex items-center justify-center text-destructive hover:bg-white"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {!isCompleted && (
            <Button onClick={completeRequest} disabled={updateMomentRequest.isPending}>
              <CheckCircle className="size-4" />
              Mark as Complete
            </Button>
          )}
          {hasVideos && !videosCleared && (
            <Button
              variant="outline"
              onClick={() => setShowClearDialog(true)}
              className="text-destructive border-destructive/40 hover:bg-destructive/10"
            >
              <ShieldAlert className="size-4" />
              Clear All Videos
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={handleDelete}
            disabled={deleteMomentRequest.isPending}
            className="text-destructive"
          >
            <Trash2 className="size-4" />
            Delete Request
          </Button>
        </div>

        {/* Clear confirmation */}
        <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Clear all videos?</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                This permanently deletes all {request.videos?.length ?? 0} video(s) from storage.
                This cannot be undone.
              </p>
              <div className="space-y-2">
                <Label htmlFor="clear-note">Audit note</Label>
                <Textarea
                  id="clear-note"
                  value={clearNote}
                  onChange={(e) => setClearNote(e.target.value)}
                  placeholder="e.g. Videos cleared after processing"
                  rows={3}
                  maxLength={500}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowClearDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleClearVideos}
                disabled={!clearNote.trim() || clearVideos.isPending}
              >
                {clearVideos.isPending ? "Clearing…" : "Clear all videos"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PageLayout>
  );
}
